/**
 * Achievements — Erfolgs-Katalog (Arsenal "Progress" tab).
 *
 * See docs/XP_PROGRESSION_PLAN.md section 7.2 for the full design
 * rationale and icon-motif choices. Each `condition(facts)` is a pure
 * function over the "facts" object assembled by the caller (js/main.js) -
 * this file never touches inventory/wallet/progress state directly, so it
 * stays trivially testable and has no import-order dependencies.
 *
 * Expected `facts` shape (all fields optional - a missing field simply
 * means that condition can't yet be true):
 * {
 *   wins, currentWinStreak, bestWinStreak,
 *   lastMatch: { isVictory, grade, plasmaUtilization, territoryControl } | null,
 *   goldCardsCount, slotsSpun, masterCategoryMaxed,
 *   targetDroneHits, bestUnitMvpCount, dailyJackpotDayCount, level
 * }
 *
 * Optional `progress: { key, target }` marks an achievement as a countable
 * milestone: `key` is a numeric field of `facts`, `target` the value at which
 * `condition` becomes true. It only drives the "Next Goals" display (see
 * getNextGoals below) - `condition` stays the single source of truth for
 * unlocking, and tests/achievements_progress_test.mjs keeps the two in sync.
 * Match-bound achievements (grade, plasma, territory) have no `progress`.
 */

import { getIconSvg } from '../ui/icons.js';

export const ACHIEVEMENT_TIERS = Object.freeze(['bronze', 'silver', 'gold', 'platinum']);

export const ACHIEVEMENT_THEMES = Object.freeze([
  {
    id: 'fauna',
    name: 'Swamp Fauna',
    icon: getIconSvg('theme_fauna', 14),
    description: 'Organic lifeforms & chitin armor of swamp biotopes'
  },
  {
    id: 'sul',
    name: 'Sul Symbiosis',
    icon: getIconSvg('theme_sul', 14),
    description: 'Biology of the Sul caste & genetic metamorphoses'
  },
  {
    id: 'ballistics',
    name: 'Ballistics & Combat',
    icon: getIconSvg('theme_ballistics', 14),
    description: 'Kinetic firepower, blades & heavy artillery'
  },
  {
    id: 'plasma',
    name: 'Plasma Technology',
    icon: getIconSvg('theme_plasma', 14),
    description: 'Fusion cores, overload & thermal plasma systems'
  },
  {
    id: 'fortress',
    name: 'Fortress Defense',
    icon: getIconSvg('theme_fortress', 14),
    description: 'Force fields, titanium bulkheads & crisis protocols'
  },
  {
    id: 'stealth',
    name: 'Stealth & Void',
    icon: getIconSvg('theme_stealth', 14),
    description: 'Cloaking, sightline tactics & shadow blades'
  },
  {
    id: 'cosmic',
    name: 'Cosmic Phenomena',
    icon: getIconSvg('theme_cosmic', 14),
    description: 'Celestial bodies, velocities & streaks'
  },
  {
    id: 'loot',
    name: 'Loot & TCG',
    icon: getIconSvg('theme_loot', 14),
    description: 'Gold cards, armored vaults & Supply Surge'
  },
  {
    id: 'fleet',
    name: 'Fleet Ranks',
    icon: getIconSvg('theme_fleet', 14),
    description: 'Command ranks, S-Rank Apex & legendary status'
  }
]);

// Platinum milestones that are earned through sustained play (1000 wins,
// a 10-win streak) pay out real XP. Only 'orbital_apex' (Level 55) is an
// endpoint and stays at 0.
const PLATINUM_MILESTONE_XP = 1000;

export const ACHIEVEMENTS = Object.freeze([
  {
    id: 'first_blood',
    name: 'First Blood',
    description: 'Win your first combat match.',
    tier: 'bronze',
    theme: 'ballistics',
    xp: 50,
    icon: 'combatBlade',
    progress: { key: 'wins', target: 1 },
    condition: (f) => (f.wins || 0) >= 1
  },
  {
    id: 's_class_commander',
    name: 'S-Rank Commander',
    description: 'Finish a match with combat rating S or S+.',
    tier: 'bronze',
    theme: 'fleet',
    xp: 50,
    icon: 'sRankCrest',
    condition: (f) => f.lastMatch && (f.lastMatch.grade === 'S' || f.lastMatch.grade === 'S+')
  },
  {
    id: 'flawless_discipline',
    name: 'Flawless Discipline',
    description: 'Achieve 100% plasma efficiency in a match.',
    tier: 'bronze',
    theme: 'plasma',
    xp: 50,
    icon: 'plasmaCore',
    // getPlasmaUtilization() (js/engine/stats.js) returns a 0-100 percentage,
    // not a 0-1 ratio.
    condition: (f) => f.lastMatch && (f.lastMatch.plasmaUtilization || 0) >= 100
  },
  {
    id: 'territorial_lord',
    name: 'Territorial Supremacy',
    description: 'Maintain over 65% average territory control in a victory.',
    tier: 'silver',
    theme: 'fleet',
    xp: 150,
    icon: 'territorialRadar',
    condition: (f) => f.lastMatch && f.lastMatch.isVictory && (f.lastMatch.territoryControl || 0) >= 65
  },
  {
    id: 'full_spectrum',
    name: 'Full Spectrum',
    description: 'Unlock all styles across any Commander cosmetic category.',
    tier: 'silver',
    theme: 'loot',
    xp: 150,
    icon: 'palette',
    condition: (f) => !!f.masterCategoryMaxed
  },
  {
    id: 'gold_rush',
    name: 'Gold Rush',
    description: 'Collect 10 Gold Edition cards.',
    tier: 'silver',
    theme: 'loot',
    xp: 150,
    icon: 'goldCard',
    progress: { key: 'goldCardsCount', target: 10 },
    condition: (f) => (f.goldCardsCount || 0) >= 10
  },
  {
    id: 'high_roller',
    name: 'High Roller',
    description: 'Execute 50 cosmetic Supply Surge spins.',
    tier: 'silver',
    theme: 'loot',
    xp: 150,
    icon: 'slotReel',
    progress: { key: 'slotsSpun', target: 50 },
    condition: (f) => (f.slotsSpun || 0) >= 50
  },
  {
    id: 'target_practice',
    name: 'Target Practice',
    description: 'Land 100 hits on the Arsenal Target Drone.',
    tier: 'bronze',
    theme: 'ballistics',
    xp: 50,
    icon: 'target',
    progress: { key: 'targetDroneHits', target: 100 },
    condition: (f) => (f.targetDroneHits || 0) >= 100
  },
  {
    id: 's_rank_chest_a9',
    name: 'S-Rank Cache: A9 Piranha',
    description: 'Claim victory with combat rating S or S+.',
    tier: 'gold',
    theme: 'loot',
    xp: 300,
    icon: 'chest',
    condition: (f) => f.lastMatch && f.lastMatch.isVictory && (f.lastMatch.grade === 'S' || f.lastMatch.grade === 'S+')
  },
  {
    id: 'win_streak_5',
    name: 'Streak X',
    description: 'Win 5 consecutive matches.',
    tier: 'gold',
    theme: 'cosmic',
    xp: 300,
    icon: 'comet',
    progress: { key: 'bestWinStreak', target: 5 },
    condition: (f) => (f.bestWinStreak || 0) >= 5
  },
  {
    id: 'unit_veteran',
    name: 'Veteran Unit',
    description: 'Win 10 matches with the same unit awarded Match MVP.',
    tier: 'gold',
    theme: 'fleet',
    xp: 300,
    icon: 'unitMvp',
    progress: { key: 'bestUnitMvpCount', target: 10 },
    condition: (f) => (f.bestUnitMvpCount || 0) >= 10
  },
  {
    id: 'daily_discipline',
    name: 'Daily Protocol',
    description: 'Complete all 3 daily quests and claim the jackpot on 7 separate days.',
    tier: 'gold',
    theme: 'cosmic',
    xp: 300,
    icon: 'dailyProtocol',
    progress: { key: 'dailyJackpotDayCount', target: 7 },
    condition: (f) => (f.dailyJackpotDayCount || 0) >= 7
  },
  // --- Milestone series (lifetime counters, tiered) ---
  {
    id: 'wins_10',
    name: 'Veteran Commander',
    description: 'Win 10 combat matches.',
    tier: 'bronze',
    theme: 'fauna',
    xp: 50,
    icon: 'critterBeetle',
    progress: { key: 'wins', target: 10 },
    condition: (f) => (f.wins || 0) >= 10
  },
  {
    id: 'wins_50',
    name: 'Seasoned Commander',
    description: 'Win 50 combat matches.',
    tier: 'silver',
    theme: 'sul',
    xp: 150,
    icon: 'critterMoth',
    progress: { key: 'wins', target: 50 },
    condition: (f) => (f.wins || 0) >= 50
  },
  {
    id: 'wins_250',
    name: 'Fleet Admiral',
    description: 'Win 250 combat matches.',
    tier: 'gold',
    theme: 'fleet',
    xp: 300,
    icon: 'shatter',
    progress: { key: 'wins', target: 250 },
    condition: (f) => (f.wins || 0) >= 250
  },
  {
    id: 'wins_1000',
    name: 'Orbital Legend',
    description: 'Win 1000 combat matches.',
    tier: 'platinum',
    theme: 'fleet',
    xp: PLATINUM_MILESTONE_XP,
    icon: 'peak',
    progress: { key: 'wins', target: 1000 },
    condition: (f) => (f.wins || 0) >= 1000
  },
  {
    id: 'level_10',
    name: 'Rookie Pilot',
    description: 'Attain Level 10.',
    tier: 'bronze',
    theme: 'fauna',
    xp: 50,
    icon: 'critterLizard',
    progress: { key: 'level', target: 10 },
    condition: (f) => (f.level || 0) >= 10
  },
  {
    id: 'level_25',
    name: 'Officer Rank',
    description: 'Attain Level 25.',
    tier: 'silver',
    theme: 'cosmic',
    xp: 150,
    icon: 'moon',
    progress: { key: 'level', target: 25 },
    condition: (f) => (f.level || 0) >= 25
  },
  {
    id: 'level_40',
    name: 'Command Rank',
    description: 'Attain Level 40.',
    tier: 'gold',
    theme: 'fleet',
    xp: 300,
    icon: 'unitMvp',
    progress: { key: 'level', target: 40 },
    condition: (f) => (f.level || 0) >= 40
  },
  {
    id: 'gold_hoarder',
    name: 'Card Hoarder',
    description: 'Collect 25 Gold Edition cards.',
    tier: 'gold',
    theme: 'loot',
    xp: 300,
    icon: 'goldCard',
    progress: { key: 'goldCardsCount', target: 25 },
    condition: (f) => (f.goldCardsCount || 0) >= 25
  },
  {
    id: 'jackpot_junkie',
    name: 'Jackpot Junkie',
    description: 'Execute 250 cosmetic Supply Surge spins.',
    tier: 'gold',
    theme: 'loot',
    xp: 300,
    icon: 'slotReel',
    progress: { key: 'slotsSpun', target: 250 },
    condition: (f) => (f.slotsSpun || 0) >= 250
  },
  {
    id: 'win_streak_10',
    name: 'Streak X-Treme',
    description: 'Win 10 consecutive matches.',
    tier: 'platinum',
    theme: 'cosmic',
    xp: PLATINUM_MILESTONE_XP,
    icon: 'twinMeteor',
    progress: { key: 'bestWinStreak', target: 10 },
    condition: (f) => (f.bestWinStreak || 0) >= 10
  },
  {
    id: 'orbital_apex',
    name: 'Orbital Apex',
    description: 'Attain Level 55.',
    tier: 'platinum',
    theme: 'fleet',
    xp: 0,
    icon: 'peak',
    progress: { key: 'level', target: 55 },
    condition: (f) => (f.level || 0) >= 55
  },
  // --- Tactical & Unit-Specific Feats ---
  {
    id: 'mist_hunter',
    name: 'Mist Hunter',
    description: 'Eliminate a cloaked enemy unit inside a dense fog bank or metamorphosis fog.',
    tier: 'silver',
    theme: 'stealth',
    xp: 150,
    icon: 'fogStalker',
    condition: (f) => !!f.lastMatch && (f.lastMatch.fogCloakKills || 0) >= 1
  },
  {
    id: 'veil_duelist',
    name: 'Shadow Across the Veil',
    description: 'Slay an enemy Anti Shade with your own Anti Shade across the shadow veil.',
    tier: 'gold',
    theme: 'stealth',
    xp: 300,
    icon: 'veilBlade',
    condition: (f) => !!f.lastMatch && (f.lastMatch.antiShadeDuelKills || 0) >= 1
  },
  {
    id: 'neural_hijack',
    name: 'Neural Hijack',
    description: 'Hijack an enemy unit worth 40+ Plasma or a Hyper unit with a Parasite Vulture.',
    tier: 'silver',
    theme: 'sul',
    xp: 150,
    icon: 'parasite',
    condition: (f) => !!f.lastMatch && (f.lastMatch.heavyUnitHijacks || 0) >= 1
  },
  {
    id: 'hyper_overdrive',
    name: 'Hyper Overdrive',
    description: 'Execute 3 or more Hyper Melds in a single victorious match.',
    tier: 'silver',
    theme: 'plasma',
    xp: 150,
    icon: 'hyperVortex',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && (f.lastMatch.meldsPerformed || 0) >= 3
  },
  {
    id: 'scrap_baron',
    name: 'Scrap Baron',
    description: 'Recover at least 50 Plasma in a single match via Salvage Company or Looting Vulture.',
    tier: 'bronze',
    theme: 'fauna',
    xp: 50,
    icon: 'critterBeetle',
    condition: (f) => !!f.lastMatch && (f.lastMatch.salvagePlasmaEarned || 0) >= 50
  },
  // --- Combat & Environmental Feats ---
  {
    id: 'collateral_carnage',
    name: 'Collateral Carnage',
    description: 'Destroy 15 enemy units with splash or explosive damage in a single match.',
    tier: 'silver',
    theme: 'ballistics',
    xp: 150,
    icon: 'clusterDetonation',
    condition: (f) => !!f.lastMatch && (f.lastMatch.splashKills || 0) >= 15
  },
  {
    id: 'no_fly_zone',
    name: 'No-Fly Zone',
    description: 'Shoot down 10 enemy air units in a single combat match.',
    tier: 'bronze',
    theme: 'ballistics',
    xp: 50,
    icon: 'noFlyZone',
    condition: (f) => !!f.lastMatch && (f.lastMatch.airKills || 0) >= 10
  },
  {
    id: 'mutant_gambit',
    name: 'Mutant Gambit',
    description: 'Trigger Experimental Metamorphosis and claim victory in the same match.',
    tier: 'silver',
    theme: 'sul',
    xp: 150,
    icon: 'critterMoth',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && (f.lastMatch.metamorphosisCast || 0) >= 1
  },
  // --- Clutch & Match Flow Feats ---
  {
    id: 'phoenix_protocol',
    name: 'Phoenix Protocol',
    description: 'Claim victory after your base integrity drops below 300 HP.',
    tier: 'gold',
    theme: 'fortress',
    xp: 300,
    icon: 'phoenixProtocol',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && (f.lastMatch.minOwnBaseHp || 0) > 0 && (f.lastMatch.minOwnBaseHp || 0) < 300
  },
  {
    id: 'impenetrable_bastion',
    name: 'Impenetrable Bastion',
    description: 'Win a combat match without your base taking a single point of damage.',
    tier: 'gold',
    theme: 'fortress',
    xp: 300,
    icon: 'bastionShield',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && (f.lastMatch.ownBaseDamageTaken || 0) === 0
  },
  {
    id: 'iron_nerves',
    name: 'Iron Nerves',
    description: 'Survive an enemy Superweapon strike and win without firing your own.',
    tier: 'gold',
    theme: 'fortress',
    xp: 300,
    icon: 'ironBunker',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && (f.lastMatch.superweaponsUsed || 0) === 0 && (f.lastMatch.enemySuperweaponsUsed || 0) >= 1
  },
  {
    id: 'lightning_blitz',
    name: 'Lightning Blitz',
    description: 'Destroy the enemy base and claim victory in under 2 minutes and 30 seconds.',
    tier: 'silver',
    theme: 'cosmic',
    xp: 150,
    icon: 'lightningBlitz',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && (f.lastMatch.duration || 0) > 0 && (f.lastMatch.duration || 0) <= 150
  },
  // --- Lifetime & Collector Milestones (Continuous Progress Tracks) ---
  {
    id: 'fugazi_tycoon',
    name: 'Fugazi Tycoon',
    description: 'Accumulate a lifetime total of 10,000 Fugazi across all operations.',
    tier: 'gold',
    theme: 'loot',
    xp: 300,
    icon: 'chest',
    progress: { key: 'lifetimeFugaziEarned', target: 10000 },
    condition: (f) => (f.lifetimeFugaziEarned || 0) >= 10000
  },
  {
    id: 'void_destroyer',
    name: 'Void Destroyer',
    description: 'Inflict a cumulative total of 100,000 combat damage across all matches.',
    tier: 'gold',
    theme: 'fleet',
    xp: 300,
    icon: 'shatter',
    progress: { key: 'lifetimeDamage', target: 100000 },
    condition: (f) => (f.lifetimeDamage || 0) >= 100000
  },
  {
    id: 'arsenal_connoisseur',
    name: 'Arsenal Connoisseur',
    description: 'Collect 25 unique cards and cosmetic Supply Surge unlocks.',
    tier: 'silver',
    theme: 'loot',
    xp: 150,
    icon: 'palette',
    progress: { key: 'uniquePerksCollected', target: 25 },
    condition: (f) => (f.uniquePerksCollected || 0) >= 25
  },
  // --- Custom Deck-Building Feats ---
  {
    id: 'custom_human_deck',
    name: 'Human Supremacy',
    description: 'Build a custom Human-only deck and claim victory in combat.',
    tier: 'silver',
    theme: 'fleet',
    xp: 150,
    icon: 'combatBlade',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && f.lastMatch.isCustomDeck && f.lastMatch.isPureHumanDeck
  },
  {
    id: 'custom_sul_deck',
    name: 'Swarm Monoculture',
    description: 'Build a custom Sul-only deck and claim victory in combat.',
    tier: 'silver',
    theme: 'sul',
    xp: 150,
    icon: 'parasite',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && f.lastMatch.isCustomDeck && f.lastMatch.isPureSulDeck
  },
  {
    id: 'custom_hybrid_deck',
    name: 'Symbiotic Balance',
    description: 'Build a custom hybrid deck (at least 40% Human and 40% Sul) and claim victory.',
    tier: 'silver',
    theme: 'cosmic',
    xp: 150,
    icon: 'twinMeteor',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && f.lastMatch.isCustomDeck && f.lastMatch.isBalancedHybridDeck
  },
  {
    id: 'custom_no_spells_deck',
    name: 'Kinetic Warfare',
    description: 'Build a custom deck containing zero spells and claim victory.',
    tier: 'silver',
    theme: 'ballistics',
    xp: 150,
    icon: 'clusterDetonation',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && f.lastMatch.isCustomDeck && f.lastMatch.hasNoSpells
  },
  {
    id: 'custom_no_structures_deck',
    name: 'Mobile Vanguard',
    description: 'Build a custom deck containing zero defensive structures and claim victory.',
    tier: 'silver',
    theme: 'fleet',
    xp: 150,
    icon: 'lightningBlitz',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && f.lastMatch.isCustomDeck && f.lastMatch.hasNoStructures
  },
  {
    id: 'custom_low_cost_deck',
    name: 'Micro Swarm',
    description: 'Build a custom deck with no card costing more than 30 Plasma and claim victory.',
    tier: 'silver',
    theme: 'plasma',
    xp: 150,
    icon: 'plasmaCore',
    condition: (f) => !!f.lastMatch && f.lastMatch.isVictory && f.lastMatch.isCustomDeck && f.lastMatch.isLowCostDeck
  }
]);

export function getAchievementById(id) {
  return ACHIEVEMENTS.find(a => a.id === id) || null;
}

const TIER_ORDER = Object.freeze(ACHIEVEMENT_TIERS.reduce((acc, t, i) => { acc[t] = i; return acc; }, {}));

/**
 * Current value/target/ratio of a countable achievement, or null when the
 * definition has no `progress` metadata. `value` is capped at `target` and
 * `ratio` at 1 so an over-achieved counter (e.g. level 60 vs. 55) never
 * renders past 100%.
 */
export function getAchievementProgress(def, facts) {
  const meta = def?.progress;
  if (!meta || !(meta.target > 0)) return null;
  const raw = Number(facts?.[meta.key]);
  const value = Math.min(meta.target, Math.max(0, Number.isFinite(raw) ? raw : 0));
  return { value, target: meta.target, ratio: value / meta.target };
}

/**
 * The next achievements the player is working towards, closest first.
 * - Unlocked and `progress`-less achievements are skipped.
 * - One goal per `progress.key` ("track"): only the lowest unearned target,
 *   so e.g. 50 / 250 / 1000 wins never occupy several slots at once.
 * - Sorted by ratio (desc), then tier (bronze first), then catalog order.
 * Returns [{ def, value, target, ratio }].
 */
export function getNextGoals(defs, facts, unlockedMap = {}, limit = 3) {
  const nextPerTrack = new Map();
  defs.forEach((def, index) => {
    if (unlockedMap?.[def.id]) return;
    const prog = getAchievementProgress(def, facts);
    if (!prog) return;
    const current = nextPerTrack.get(def.progress.key);
    if (!current || prog.target < current.target) {
      nextPerTrack.set(def.progress.key, { def, index, ...prog });
    }
  });
  return [...nextPerTrack.values()]
    .sort((a, b) =>
      (b.ratio - a.ratio) ||
      ((TIER_ORDER[a.def.tier] ?? 0) - (TIER_ORDER[b.def.tier] ?? 0)) ||
      (a.index - b.index))
    .slice(0, Math.max(0, limit))
    .map(({ def, value, target, ratio }) => ({ def, value, target, ratio }));
}
